import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'
import { useGroup } from '../lib/GroupContext'
import { getMovieDetails, posterUrl } from '../lib/tmdb'
import { getWarningsForMovie } from '../lib/dtdd'
import { track, Events } from '../lib/analytics'
import Page from '../components/Page'
import TabBar from '../components/TabBar'

export default function MovieDetail() {
  const { tmdbId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { activeGroup } = useGroup()
  const [movie, setMovie] = useState(null)
  const [loading, setLoading] = useState(true)
  const [adding, setAdding] = useState(false)
  const [alreadyAdded, setAlreadyAdded] = useState(false)
  const [activeSession, setActiveSession] = useState(null)
  const [isMyPick, setIsMyPick] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [contentWarnings, setContentWarnings] = useState(null)
  const [warningsExpanded, setWarningsExpanded] = useState(false)

  useEffect(() => {
    loadMovie()
  }, [tmdbId])

  async function loadMovie() {
    setLoading(true)
    setContentWarnings(null)
    try {
      const details = await getMovieDetails(parseInt(tmdbId))
      setMovie(details)

      // Load content warnings in background (non-blocking)
      getWarningsForMovie(details.title, details.tmdb_id).then(result => {
        setContentWarnings(result)
      }).catch(() => {})

      // Check if already in watchlist
      const { data: existing } = await supabase
        .from('movies')
        .select('id')
        .eq('tmdb_id', parseInt(tmdbId))
        .maybeSingle()

      if (existing) {
        const { data: inList } = await supabase
          .from('watchlist_items')
          .select('id')
          .eq('user_id', user.id)
          .eq('movie_id', existing.id)
          .maybeSingle()
        if (inList) setAlreadyAdded(true)
      }

      // Check for active movie night session
      if (activeGroup) {
        const { data: session } = await supabase
          .from('movie_night_sessions')
          .select('*')
          .eq('group_id', activeGroup.id)
          .in('status', ['open', 'spinning'])
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle()

        setActiveSession(session || null)

        if (session && existing) {
          const { data: sub } = await supabase
            .from('session_submissions')
            .select('id, movie_id')
            .eq('session_id', session.id)
            .eq('user_id', user.id)
            .maybeSingle()
          if (sub && sub.movie_id === existing.id) setIsMyPick(true)
        }
      }
    } catch (err) {
      console.error(err)
    }
    setLoading(false)
  }

  // Upsert movie into DB and return the record
  async function ensureMovieRecord() {
    // Only include columns that exist in the DB schema
    const upsertData = {
      tmdb_id: movie.tmdb_id,
      title: movie.title,
      poster_url: movie.poster_url,
      year: movie.year,
      genre: movie.genre,
      runtime_minutes: movie.runtime_minutes,
      tmdb_rating: movie.tmdb_rating,
      overview: movie.overview,
    }
    // These columns may have been added later — include only if they exist
    if (movie.content_rating) upsertData.content_rating = movie.content_rating
    if (movie.genres?.length) upsertData.genres = movie.genres

    const { data: movieRecord, error: movieErr } = await supabase
      .from('movies')
      .upsert(upsertData, { onConflict: 'tmdb_id' })
      .select()
      .single()
    if (movieErr) throw movieErr
    return movieRecord
  }

  async function handleAdd() {
    if (!movie || adding) return
    setAdding(true)
    try {
      const movieRecord = await ensureMovieRecord()
      await supabase.from('watchlist_items').insert({
        user_id: user.id,
        movie_id: movieRecord.id,
      })
      track(Events.WatchlistAdded, { tmdb_id: movie.tmdb_id })
      setAlreadyAdded(true)
      setAdding(false)
    } catch (err) {
      console.error(err)
      alert('Failed to add. Try again.')
      setAdding(false)
    }
  }

  async function handlePickForTonight() {
    if (!movie || !activeSession || submitting) return
    setSubmitting(true)
    try {
      // Ensure movie exists in DB
      const movieRecord = await ensureMovieRecord()

      // Also add to watchlist if not already there (non-blocking — don't let this fail the pick)
      if (!alreadyAdded) {
        try {
          // Check if already in watchlist first, then insert if not
          const { data: existingWl } = await supabase
            .from('watchlist_items')
            .select('id')
            .eq('user_id', user.id)
            .eq('movie_id', movieRecord.id)
            .maybeSingle()
          if (!existingWl) {
            await supabase.from('watchlist_items').insert({
              user_id: user.id,
              movie_id: movieRecord.id,
            })
          }
          setAlreadyAdded(true)
        } catch (wlErr) {
          console.warn('Watchlist add failed (non-critical):', wlErr)
        }
      }

      // Check for existing submission
      const { data: existingSub } = await supabase
        .from('session_submissions')
        .select('id')
        .eq('session_id', activeSession.id)
        .eq('user_id', user.id)
        .maybeSingle()

      if (existingSub) {
        await supabase
          .from('session_submissions')
          .update({ movie_id: movieRecord.id })
          .eq('id', existingSub.id)
      } else {
        await supabase.from('session_submissions').insert({
          session_id: activeSession.id,
          user_id: user.id,
          movie_id: movieRecord.id,
        })
      }

      track(Events.PicksSubmitted, { tmdb_id: movie.tmdb_id, replaced: Boolean(existingSub) })
      setIsMyPick(true)
      setSubmitting(false)
    } catch (err) {
      console.error('Pick submission failed:', err?.message || err, err)
      alert('Could not submit pick. Try again.')
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center">
        <span className="text-[var(--fp-text-muted)]">Loading...</span>
      </div>
    )
  }

  if (!movie) {
    return (
      <Page mode="cinema">
        <p className="text-[var(--fp-text-muted)]">Movie not found.</p>
        <button onClick={() => navigate(-1)} className="fp-btn fp-btn-secondary mt-6" style={{ width: 'auto', padding: '14px 32px' }}>
          Go back
        </button>
      </Page>
    )
  }

  const runtime = movie.runtime_minutes
    ? `${Math.floor(movie.runtime_minutes / 60)}h ${movie.runtime_minutes % 60}m`
    : null

  return (
    <div className="min-h-[100dvh] relative w-full flex justify-center">
      {/* Hero backdrop — blurred poster */}
      {movie.poster_url && (
        <div className="absolute inset-0 h-[480px] overflow-hidden z-0">
          <img
            src={movie.poster_url}
            alt=""
            className="w-full h-full object-cover"
            style={{ filter: 'blur(40px) brightness(0.3)', transform: 'scale(1.3)' }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[rgba(0,0,0,0.7)] to-[var(--fp-bg)]" />
        </div>
      )}

      <div className="relative z-10 w-full max-w-2xl px-8 pt-8 pb-48 sm:px-12">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="text-sm font-semibold text-[var(--fp-text-muted)] bg-transparent border-none cursor-pointer mb-10 hover:text-white"
        >
          ← Back to search
        </button>

        {/* Poster + Title lockup */}
        <div className="flex flex-col sm:flex-row gap-10 items-center sm:items-start mb-14">
          <div
            className="w-48 flex-shrink-0 rounded-2xl overflow-hidden shadow-2xl mx-auto sm:mx-0"
            style={{
              aspectRatio: '2/3',
              border: '2px solid var(--fp-border)',
              boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
            }}
          >
            {movie.poster_url ? (
              <img src={movie.poster_url} alt={movie.title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-6xl bg-[var(--fp-surface)]">🎬</div>
            )}
          </div>

          <div className="flex-1 w-full text-center sm:text-left">
            <h1 className="fp-display text-4xl text-white mb-4" style={{ lineHeight: '1.05' }}>
              {movie.title}
            </h1>
            <div className="flex flex-wrap gap-3 items-center justify-center sm:justify-start text-sm text-[var(--fp-text-muted)] mb-6">
              {movie.content_rating && (
                <span
                  className="text-xs font-bold px-2 py-0.5 rounded border"
                  style={{
                    borderColor: 'var(--fp-border-accent)',
                    color: '#fff',
                    background: 'var(--fp-surface)',
                    letterSpacing: '0.03em',
                  }}
                >
                  {movie.content_rating}
                </span>
              )}
              {movie.year && <span>{movie.year}</span>}
              {runtime && <><span>·</span><span>{runtime}</span></>}
              {movie.tmdb_rating && (
                <>
                  <span>·</span>
                  <span className="fp-gold-text font-semibold">★ {movie.tmdb_rating}</span>
                </>
              )}
            </div>
            {movie.genres?.length > 0 && (
              <div className="flex flex-wrap gap-2 justify-center sm:justify-start mb-8">
                {movie.genres.map(g => (
                  <span
                    key={g}
                    className="text-xs font-semibold px-3 py-1.5 rounded-full border border-[var(--fp-border-accent)] text-[var(--fp-text-muted)]"
                  >
                    {g}
                  </span>
                ))}
              </div>
            )}
            {movie.director && (
              <p className="text-sm text-[var(--fp-text-muted)] mb-6">
                Directed by{' '}
                <button
                  onClick={() => navigate('/add', { state: { person: { id: movie.director.id, name: movie.director.name, profile_url: null } } })}
                  className="text-white font-semibold bg-transparent border-none cursor-pointer hover:text-[var(--fp-red)] transition-colors p-0 text-sm underline underline-offset-2 decoration-[var(--fp-border-accent)]"
                >
                  {movie.director.name} →
                </button>
              </p>
            )}

            {/* Action buttons — right below the movie info */}
            <div className="flex flex-col gap-3 w-full">
              {activeSession && !isMyPick && (
                <button
                  onClick={handlePickForTonight}
                  disabled={submitting}
                  className="fp-btn fp-btn-gold w-full"
                  style={{
                    padding: '16px 24px',
                    fontSize: '15px',
                    boxShadow: '0 12px 40px rgba(212, 175, 55, 0.35)',
                  }}
                >
                  {submitting ? 'Submitting…' : 'Pick this for tonight'}
                </button>
              )}

              {activeSession && isMyPick && (
                <p className="text-sm font-semibold" style={{ color: 'var(--fp-gold)' }}>
                  ★ This is your pick tonight
                </p>
              )}

              {alreadyAdded ? (
                <p className="text-sm text-[var(--fp-text-dim)]">In your watchlist ✓</p>
              ) : (
                <button
                  onClick={handleAdd}
                  disabled={adding}
                  className={`fp-btn w-full ${activeSession ? 'fp-btn-secondary' : 'fp-btn-primary'}`}
                  style={!activeSession ? { boxShadow: '0 12px 40px var(--fp-red-glow)' } : {}}
                >
                  {adding ? 'Adding…' : 'Add to Watchlist'}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Trailer */}
        {movie.trailer_key && (
          <div className="mb-14">
            <div className="text-[11px] fp-gold-text uppercase tracking-[0.25em] font-semibold mb-5 pl-1">
              Trailer
            </div>
            <div
              className="rounded-2xl overflow-hidden bg-black"
              style={{
                aspectRatio: '16/9',
                boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
                border: '1px solid var(--fp-border)',
              }}
            >
              <iframe
                src={`https://www.youtube.com/embed/${movie.trailer_key}?rel=0&modestbranding=1`}
                title="Trailer"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full border-none"
              />
            </div>
          </div>
        )}

        {/* Synopsis */}
        {movie.overview && (
          <div className="mb-14">
            <div className="text-[11px] fp-gold-text uppercase tracking-[0.25em] font-semibold mb-5 pl-1">
              Synopsis
            </div>
            <p className="text-base text-[var(--fp-text-muted)] leading-relaxed" style={{ lineHeight: '1.8' }}>
              {movie.overview}
            </p>
          </div>
        )}

        {/* Where to Watch */}
        {movie.watch_providers && (movie.watch_providers.flatrate?.length > 0 || movie.watch_providers.rent?.length > 0 || movie.watch_providers.buy?.length > 0) && (
          <div className="mb-14">
            <div className="text-[11px] fp-gold-text uppercase tracking-[0.25em] font-semibold mb-5 pl-1">
              Where to Watch
            </div>
            <div className="fp-card">
              {movie.watch_providers.flatrate?.length > 0 && (
                <div className="mb-6 last:mb-0">
                  <p className="text-xs text-[var(--fp-text-muted)] mb-3 uppercase tracking-wider font-semibold">Stream</p>
                  <div className="flex flex-wrap gap-4">
                    {movie.watch_providers.flatrate.map((p, i) => (
                      <div key={i} className="flex flex-col items-center gap-2">
                        {p.logo_url ? (
                          <img src={p.logo_url} alt={p.name} className="w-12 h-12 rounded-xl" />
                        ) : (
                          <div className="w-12 h-12 rounded-xl bg-[var(--fp-border)] flex items-center justify-center text-xs text-[var(--fp-text-dim)]">?</div>
                        )}
                        <span className="text-[10px] text-[var(--fp-text-muted)] text-center max-w-[60px] truncate">{p.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {movie.watch_providers.rent?.length > 0 && (
                <div className="mb-6 last:mb-0">
                  <p className="text-xs text-[var(--fp-text-muted)] mb-3 uppercase tracking-wider font-semibold">Rent</p>
                  <div className="flex flex-wrap gap-4">
                    {movie.watch_providers.rent.map((p, i) => (
                      <div key={i} className="flex flex-col items-center gap-2">
                        {p.logo_url ? (
                          <img src={p.logo_url} alt={p.name} className="w-12 h-12 rounded-xl" />
                        ) : (
                          <div className="w-12 h-12 rounded-xl bg-[var(--fp-border)] flex items-center justify-center text-xs text-[var(--fp-text-dim)]">?</div>
                        )}
                        <span className="text-[10px] text-[var(--fp-text-muted)] text-center max-w-[60px] truncate">{p.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {movie.watch_providers.buy?.length > 0 && (
                <div className="mb-0">
                  <p className="text-xs text-[var(--fp-text-muted)] mb-3 uppercase tracking-wider font-semibold">Buy</p>
                  <div className="flex flex-wrap gap-4">
                    {movie.watch_providers.buy.map((p, i) => (
                      <div key={i} className="flex flex-col items-center gap-2">
                        {p.logo_url ? (
                          <img src={p.logo_url} alt={p.name} className="w-12 h-12 rounded-xl" />
                        ) : (
                          <div className="w-12 h-12 rounded-xl bg-[var(--fp-border)] flex items-center justify-center text-xs text-[var(--fp-text-dim)]">?</div>
                        )}
                        <span className="text-[10px] text-[var(--fp-text-muted)] text-center max-w-[60px] truncate">{p.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <p className="mt-5 pt-3 border-t border-[var(--fp-border)] text-right" style={{ fontSize: '8px', color: 'var(--fp-border-accent)', letterSpacing: '0.05em' }}>
                JustWatch / TMDB
              </p>
            </div>
          </div>
        )}

        {/* Cast */}
        {movie.cast?.length > 0 && (
          <div className="mb-16">
            <div className="text-[11px] fp-gold-text uppercase tracking-[0.25em] font-semibold mb-5 pl-1">
              Cast
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
              {movie.cast.map((c, i) => (
                <button
                  key={i}
                  className="text-center bg-transparent border-none cursor-pointer p-2 rounded-xl hover:bg-[var(--fp-elevated)] transition-colors"
                  onClick={() => navigate('/add', { state: { person: { id: c.id, name: c.name, profile_url: c.profile_url } } })}
                >
                  <div className="w-20 h-20 rounded-full overflow-hidden mx-auto mb-3 bg-[var(--fp-border)]">
                    {c.profile_url ? (
                      <img src={c.profile_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-2xl text-[var(--fp-text-dim)]">👤</div>
                    )}
                  </div>
                  <p className="text-sm font-semibold text-white truncate">{c.name}</p>
                  <p className="text-xs text-[var(--fp-text-dim)] truncate mt-1">{c.character}</p>
                  <p className="text-[10px] text-[var(--fp-red)] mt-1.5">View filmography →</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Content Warnings (DoesTheDogDie) */}
        {contentWarnings && contentWarnings.warnings.length > 0 && (() => {
          // Sort: sensitive (red flags) first, then by vote count
          const sorted = [...contentWarnings.warnings].sort((a, b) => {
            if (a.isSensitive !== b.isSensitive) return b.isSensitive ? 1 : -1
            return b.yesCount - a.yesCount
          })
          const sensitiveCount = sorted.filter(w => w.isSensitive).length
          // Always show all sensitive warnings + at least 4 non-sensitive, minimum 8
          const defaultVisible = Math.max(sensitiveCount + 4, 8)
          const visible = warningsExpanded ? sorted : sorted.slice(0, defaultVisible)
          const hiddenCount = sorted.length - defaultVisible

          return (
            <div className="mb-16">
              <div className="text-[11px] fp-gold-text uppercase tracking-[0.25em] font-semibold mb-5 pl-1">
                Content Warnings
              </div>
              <div className="fp-card">
                {/* Sensitive warnings — grouped first with label */}
                {sensitiveCount > 0 && (
                  <div className="mb-4">
                    <p className="text-[10px] uppercase tracking-wider font-semibold mb-3" style={{ color: 'var(--fp-red)' }}>
                      Sensitive content flagged
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {sorted.filter(w => w.isSensitive).map((w, i) => (
                        <span
                          key={`s-${i}`}
                          className="text-xs px-3 py-1.5 rounded-full border flex items-center gap-1.5"
                          style={{
                            borderColor: 'var(--fp-red)',
                            color: '#ff8a8a',
                            background: 'rgba(200, 16, 46, 0.1)',
                          }}
                        >
                          <span>⚠️</span>
                          <span className="capitalize">{w.name}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Non-sensitive warnings */}
                {sorted.some(w => !w.isSensitive) && (
                  <div>
                    {sensitiveCount > 0 && (
                      <p className="text-[10px] uppercase tracking-wider font-semibold mb-3 mt-4" style={{ color: 'var(--fp-text-dim)' }}>
                        Also flagged
                      </p>
                    )}
                    <div className="flex flex-wrap gap-2">
                      {visible.filter(w => !w.isSensitive).map((w, i) => (
                        <span
                          key={`n-${i}`}
                          className="text-xs px-3 py-1.5 rounded-full border flex items-center gap-1.5"
                          style={{
                            borderColor: 'var(--fp-border-accent)',
                            color: 'var(--fp-text-muted)',
                            background: 'var(--fp-surface)',
                          }}
                        >
                          <span>⚡</span>
                          <span className="capitalize">{w.name}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {hiddenCount > 0 && !warningsExpanded && (
                  <button
                    onClick={() => setWarningsExpanded(true)}
                    className="text-xs text-[var(--fp-text-dim)] bg-transparent border-none cursor-pointer mt-4 hover:text-white transition-colors"
                  >
                    Show {hiddenCount} more →
                  </button>
                )}
                <p className="mt-4 pt-3 border-t border-[var(--fp-border)] text-right" style={{ fontSize: '8px', color: 'var(--fp-border-accent)', letterSpacing: '0.05em' }}>
                  Community data from DoesTheDogDie.com
                </p>
              </div>
            </div>
          )
        })()}

        {/* Bottom action CTAs (repeated for convenience after scrolling) */}
        <div className="max-w-sm mx-auto pt-4 flex flex-col gap-4">
          {activeSession && !isMyPick && (
            <button
              onClick={handlePickForTonight}
              disabled={submitting}
              className="fp-btn fp-btn-gold w-full"
              style={{
                padding: '22px 24px',
                fontSize: '17px',
                boxShadow: '0 12px 40px rgba(212, 175, 55, 0.35)',
              }}
            >
              {submitting ? 'Submitting…' : 'Pick this for tonight'}
            </button>
          )}

          {activeSession && isMyPick && (
            <div className="text-center">
              <p className="text-base font-semibold" style={{ color: 'var(--fp-gold)' }}>
                ★ This is your pick tonight
              </p>
              <button
                onClick={() => navigate('/')}
                className="text-sm text-[var(--fp-text-muted)] bg-transparent border-none cursor-pointer mt-3 hover:text-white transition-colors"
              >
                Back to the crew →
              </button>
            </div>
          )}

          {alreadyAdded ? (
            <div className="text-center">
              <p className="text-sm text-[var(--fp-text-dim)]">In your watchlist ✓</p>
            </div>
          ) : (
            <button
              onClick={handleAdd}
              disabled={adding}
              className={`fp-btn w-full ${activeSession ? 'fp-btn-secondary' : 'fp-btn-primary'}`}
              style={!activeSession ? { boxShadow: '0 12px 40px var(--fp-red-glow)' } : {}}
            >
              {adding ? 'Adding…' : 'Add to Watchlist'}
            </button>
          )}
        </div>

      </div>
      <TabBar />
    </div>
  )
}
